"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/account/Navbar";
import Footer from "../../components/account/Footer";
import { handleApiError } from "../../utils/apiUtils";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import { socialAPI, notificationsAPI, petGuardianshipAPI } from "../../services/api";
import notificationService from "../../services/notificationService";
import { useAuth } from "../../hooks/useAuth";
import { USER_TYPES } from "../../constants/userTypes";
import { usePostModal } from "../../contexts/PostModalContext";
import { handleNotificationRouting, isNotificationClickable } from "../../utils/notificationRouting";
import "./NotificationsPage.css";

const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { getUserType, updateNotificationCount } = useAuth();
  const { openPostModal } = usePostModal();
  const userType = getUserType();

  // Get clinicId from URL params or try to extract from current path
  const getClinicId = () => {
    if (params?.clinicId) return params.clinicId;
    const pathMatch = window.location.pathname.match(/\/medical\/clinic\/(\d+)/);
    if (pathMatch) return pathMatch[1];
    try {
      const userData = localStorage.getItem('user_data');
      if (userData && userData !== 'undefined' && userData !== 'null') {
        const parsed = JSON.parse(userData);
        return parsed.clinic_id;
      }
    } catch (e) {
      // Ignore errors
    }
    return null;
  };

  const getUserTypeColorScheme = (userType) => {
    switch (userType) {
      case "PET_GUARDIAN":
        return { primary: "#8548A8", hover: "#7A4397" };
      case "VETERINARIAN":
        return { primary: "#00D4B3", hover: "#00C4A7" };
      case "SERVICE_PROVIDER":
        return { primary: "#D65745", hover: "#C7493A" };
      default:
        return { primary: "#8548A8", hover: "#7A4397" };
    }
  };

  const colors = getUserTypeColorScheme(userType);

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [markingAsRead, setMarkingAsRead] = useState(null);
  const [markingAsUnread, setMarkingAsUnread] = useState(null);
  const [processingRequest, setProcessingRequest] = useState(null); // Track which follow request is being processed
  const [pendingFollowRequests, setPendingFollowRequests] = useState([]);

  // Pagination State
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadMore, setIsLoadMore] = useState(false);

  const intervalRef = useRef(null);

  // Fetch all notifications
  // Fetch notifications
  const fetchAllNotifications = useCallback(async (isDataLoadMore = false) => {
    try {
      if (isDataLoadMore) {
        setIsLoadMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const currentPage = isDataLoadMore ? page + 1 : 1;

      // Build query params with filter
      const params = {
        per_page: 20,
        page: currentPage
      };

      // Add filter to API call if not "all"
      if (filter === "unread") {
        params.status = "unread";
      } else if (filter === "read") {
        params.status = "read";
      }

      const response = await notificationsAPI.getNotificationsWithRead("app", params);
      const result = response.data;

      // Extract merged follow requests (only on first page load)
      if (!isDataLoadMore) {
        const followRequests = result?.follow_requests?.data?.requesters || [];
        setPendingFollowRequests(Array.isArray(followRequests) ? followRequests : []);

        // Update navbar notification count from API response if available
        if (result?.unread_notifications_count_in_drawer !== undefined && updateNotificationCount) {
          updateNotificationCount(result.unread_notifications_count_in_drawer);
        } else if (result?.unread_notification_count !== undefined && updateNotificationCount) {
          updateNotificationCount(result.unread_notification_count);
        }
      }

      // Extract the notifications array from the response
      const notificationsArray = result?.data || [];

      // Update hasMore based on whether we received fewer items than requested
      // If we got 0 items, definitely no more. If we got < 20, likely no more.
      if (notificationsArray.length < 20) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      // Parse notifications using the service
      const parsedNotifications = notificationService.parseNotifications(notificationsArray);

      if (isDataLoadMore) {
        setNotifications(prev => {
          // Filter out duplicates based on ID
          const existingIds = new Set(prev.map(n => n.id));
          const newUnique = parsedNotifications.filter(n => !existingIds.has(n.id));
          const combined = [...prev, ...newUnique];

          // Re-sort
          return combined.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0);
            const dateB = new Date(b.created_at || b.createdAt || 0);
            return dateB - dateA;
          });
        });
        setPage(currentPage);
      } else {
        // Sort by created_at descending (newest first)
        parsedNotifications.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || 0);
          const dateB = new Date(b.created_at || b.createdAt || 0);
          return dateB - dateA;
        });
        setNotifications(parsedNotifications);
        setPage(1);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      handleApiError(error, setError);
    } finally {
      setIsLoading(false);
      setIsLoadMore(false);
    }
  }, [page, filter, updateNotificationCount]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      setMarkingAsRead(notificationId);
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
      const token = localStorage.getItem("access_token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/notifications/${notificationId}/mark-read`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, status: "read", is_read: true }
              : notif
          )
        );
      } else {
        throw new Error(`Failed to mark notification as read: ${response.status}`);
      }
    } catch (error) {
      handleApiError(error, setError);
      showErrorToast("Failed to mark notification as read");
    } finally {
      setMarkingAsRead(null);
    }
  }, []);


  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
      const token = localStorage.getItem("access_token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/notifications/mark-all-read`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        showSuccessToast("All notifications marked as read");
        // Update navbar notification count to 0 (all are now read)
        if (updateNotificationCount) {
          updateNotificationCount(0);
        }
        // Refresh the entire list to reflect changes (marks ALL notifications, not just loaded ones)
        setPage(1);
        setHasMore(true);
        fetchAllNotifications(false);
      } else {
        throw new Error(`Failed to mark all as read: ${response.status}`);
      }
    } catch (error) {
      handleApiError(error, setError);
      showErrorToast("Failed to mark all notifications as read");
    }
  }, [filter, fetchAllNotifications]);

  // Delete all read notifications
  const deleteAllRead = useCallback(async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
      const token = localStorage.getItem("access_token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/notifications/delete-read`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        showSuccessToast("All read notifications deleted");
        // Refresh the entire list to reflect changes
        // The count will be updated automatically when fetchAllNotifications completes
        // because it reads unread_notifications_count_in_drawer from the API response
        setPage(1);
        setHasMore(true);
        fetchAllNotifications(false);
      } else {
        throw new Error(`Failed to delete read notifications: ${response.status}`);
      }
    } catch (error) {
      handleApiError(error, setError);
      showErrorToast("Failed to delete read notifications");
    }
  }, [filter, fetchAllNotifications]);

  // Handle follow request approval/rejection (matching NotificationDropdown logic)
  const handleFollowRequest = async (requestId, action, notificationId = null) => {
    try {
      setError(null);
      setProcessingRequest(notificationId || requestId);

      // Call the appropriate API method
      const response = action === 'approve'
        ? await socialAPI.acceptConnectionRequest(requestId)
        : await socialAPI.rejectConnectionRequest(requestId);


      // Update the notifications state - mark as processed
      setNotifications(prevNotifications => {
        return prevNotifications.map(notif => {
          // Check if this is the notification we're processing
          const isTargetNotification = notificationId
            ? notif.id === notificationId
            : (() => {
              const senderId = notif.sender_id ||
                notif.parsedData?.sender_id ||
                notif.parsedData?.user_id ||
                notif.sender_Data?.id ||
                notif.parsedData?.content_id ||
                notif.target_id;
              return senderId === requestId;
            })();

          if (isTargetNotification) {
            // Mark notification as processed and update the text
            return {
              ...notif,
              processed: true,
              processedAction: action,
              mainText: action === 'approve'
                ? `${notif.senderInfo || notif.parsedData?.sender_name || 'Buddy request'} accepted`
                : `${notif.senderInfo || notif.parsedData?.sender_name || 'Buddy request'} rejected`
            };
          }
          return notif;
        });
      });

      setPendingFollowRequests(prev => prev.filter(req => req.id !== requestId));

      // Remove processed notification after showing the status for 2 seconds
      setTimeout(() => {
        setNotifications(current =>
          current.filter(notif => {
            if (notificationId) {
              return notif.id !== notificationId;
            }
            const senderId = notif.sender_id ||
              notif.parsedData?.sender_id ||
              notif.parsedData?.user_id ||
              notif.sender_Data?.id ||
              notif.parsedData?.content_id ||
              notif.target_id;
            return senderId !== requestId;
          })
        );
      }, 2000);

      // Show success toast
      const actionText = action === "approve" ? "accepted" : "rejected";
      // Note: Toast is commented out in NotificationDropdown, keeping it here for user feedback
      // showSuccessToast(`Connection request ${actionText} successfully!`, {
      //   title: "Connection Request",
      //   duration: 3000,
      // });
    } catch (error) {
      console.error(`Error ${action}ing follow request:`, error);
      showErrorToast(`Failed to ${action} buddy request`, {
        title: "Error",
        duration: 4000,
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle guardianship invitation response
  const handleGuardianshipResponse = async (requestId, action, notificationId = null) => {
    try {
      setError(null);
      setProcessingRequest(notificationId || requestId);

      const actionType = action === 'approve' ? 'accepted' : 'rejected';
      await petGuardianshipAPI.respondToRequest(requestId, actionType);

      // Update local state - mark as processed
      setNotifications(prevNotifications => {
        return prevNotifications.map(notif => {
          if (notif.id === notificationId) {
            return {
              ...notif,
              processed: true,
              processedAction: action,
              mainText: action === 'approve'
                ? `Guardianship invitation accepted`
                : `Guardianship invitation rejected`
            };
          }
          return notif;
        });
      });

      // Remove processed notification after showing the status for 2 seconds
      setTimeout(() => {
        setNotifications(current => current.filter(notif => notif.id !== notificationId));
      }, 2000);

      // Show success toast
      showSuccessToast(action === "approve" ? "Successfully joined as a guardian!" : "Invitation declined.");
    } catch (error) {
      console.error(`Error ${action}ing guardianship invite:`, error);
      showErrorToast(`Failed to ${action} guardianship invite`);
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle notification click - using centralized routing utility
  const handleNotificationClick = (notification) => {
    // Don't handle click for notifications that aren't clickable (have their own buttons)
    if (!isNotificationClickable(notification.type)) {
      return;
    }

    // Mark as read if unread
    if (notification.status === "unread" || !notification.is_read) {
      markAsRead(notification.id);
    }

    // Use centralized routing utility
    const handled = handleNotificationRouting(
      notification,
      navigate,
      () => { }, // onClose callback (not needed for page)
      openPostModal
    );

    // If routing wasn't handled, at least mark as read
    if (!handled && (notification.status === "unread" || !notification.is_read)) {
      markAsRead(notification.id);
    }
  };

  // Format date for grouping
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Unknown";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Unknown";

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);

      if (dateOnly.getTime() === today.getTime()) {
        return "Today";
      } else if (dateOnly.getTime() === yesterday.getTime()) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
        });
      }
    } catch (error) {
      return "Unknown";
    }
  }, []);

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Format notification message - replace "follow request" with "buddy request"
  const formatNotificationMessage = (notification) => {
    const message = notification.content || notification.message || notification.body || "No message";
    if (notification.type === "new_follow_request") {
      return message.replace(/follow request/gi, "buddy request");
    }
    return message;
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "new_follow_request":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="8.5" cy="7" r="4"></circle>
            <line x1="20" y1="8" x2="20" y2="14"></line>
            <line x1="23" y1="11" x2="17" y2="11"></line>
          </svg>
        );
      case "clinic_invitation":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
        );
      case "urgent_case":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        );
      case "message":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        );
    }
  };

  // Get notification color based on type
  const getNotificationColor = (type) => {
    switch (type) {
      case "new_follow_request":
        return "#3B82F6";
      case "clinic_invitation":
        return "#10B981";
      case "urgent_case":
        return "#EF4444";
      case "message":
        return "#8B5CF6";
      default:
        return "#6B7280";
    }
  };

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter((notif) => {
      const isRead = notif.status === "read" || notif.is_read === true;
      if (filter === "unread") return !isRead;
      if (filter === "read") return isRead;
      return true; // all
    });
  }, [notifications, filter]);

  // Group notifications by date
  const groupedNotifications = useMemo(() => {
    return filteredNotifications.reduce((groups, notif) => {
      const date = formatDate(notif.created_at || notif.createdAt || new Date());
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notif);
      return groups;
    }, {});
  }, [filteredNotifications, formatDate]);

  // Load notifications on mount and when filter changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchAllNotifications(false);

    // Removed polling to prevent list jumps during pagination
    return () => { };
  }, [filter]); // Reload when filter changes

  // Load notifications on mount
  useEffect(() => {
    if (notifications.length === 0) {
      fetchAllNotifications(false);
    }
  }, []);

  const unreadCount = notifications.filter(
    (notif) => notif.status === "unread" || !notif.is_read
  ).length;

  const readCount = notifications.filter(
    (notif) => notif.status === "read" || notif.is_read === true
  ).length;

  return (
    <div
      className="notifications-page"
      style={{
        "--notifications-primary-color": colors.primary,
        "--notifications-hover-color": colors.hover,
      }}
    >
      <Navbar />
      <div className="notifications-page-container">
        <div className="notifications-page-header">
          <div className="notifications-page-header-content">
            <h1 className="notifications-page-title">
              {t("notifications.page.title")}
            </h1>
            <p className="notifications-page-subtitle">
              {t("notifications.page.subtitle")}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="notifications-page-filters" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`notifications-page-filter-btn ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              {t("notifications.filters.all")}
              {filter === "all" && (
                <span className="notifications-page-filter-count">
                  {notifications.length}
                </span>
              )}
            </button>
            <button
              className={`notifications-page-filter-btn ${filter === "unread" ? "active" : ""}`}
              onClick={() => setFilter("unread")}
            >
              {t("notifications.filters.unread")}
              {unreadCount > 0 && (
                <span className="notifications-page-filter-count unread">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              className={`notifications-page-filter-btn ${filter === "read" ? "active" : ""}`}
              onClick={() => setFilter("read")}
            >
              {t("notifications.filters.read")}
              {filter === "read" && (
                <span className="notifications-page-filter-count">
                  {notifications.length - unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {unreadCount > 0 && (filter === "all" || filter === "unread") && (
              <button
                className="notifications-page-mark-all-btn"
                onClick={markAllAsRead}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {t("notifications.page.markAll")}
              </button>
            )}
            {readCount > 0 && (filter === "all" || filter === "read") && (
              <button
                className="notifications-page-mark-all-btn"
                onClick={deleteAllRead}
                style={{
                  backgroundColor: 'transparent',
                  color: '#EF4444',
                  border: '1px solid #EF4444'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                {t("notifications.page.deleteRead")}
              </button>
            )}
          </div>
        </div>

        {/* Follow Requests Section */}
        {pendingFollowRequests.length > 0 && (
          <div className="notifications-page-follow-requests">
            <div className="notifications-page-section-header">
              {t("Pending_Follow_Requests")} ({pendingFollowRequests.length})
            </div>
            <div className="notifications-page-follow-request-list">
              {pendingFollowRequests.map((request) => (
                <div key={request.id || request.user_id} className="notifications-page-follow-request-item">
                  <div className="notifications-page-avatar">
                    {request.profile_image_url ? (
                      <img src={request.profile_image_url} alt={request.first_name || "User"} />
                    ) : (
                      <div className="notifications-page-avatar-fallback">
                        {(request.first_name ? request.first_name[0] : "U").toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="notifications-page-follow-request-body">
                    <div className="notifications-page-follow-request-name">
                      {request.full_name || `${request.first_name || ""} ${request.last_name || ""}`.trim() || request.username}
                    </div>
                    <div className="notifications-page-follow-request-subtitle">
                      {t("followrequest")}
                    </div>
                  </div>
                  <div className="notifications-page-follow-request-actions">
                    <button
                      onClick={() => handleFollowRequest(request.id, "approve")}
                      className="notifications-page-follow-request-btn accept"
                      disabled={processingRequest === request.id}
                    >
                      {processingRequest === request.id ? (
                        <div className="notifications-page-spinner-small" />
                      ) : (
                        t("Accept")
                      )}
                    </button>
                    <button
                      onClick={() => handleFollowRequest(request.id, "reject")}
                      className="notifications-page-follow-request-btn reject"
                      disabled={processingRequest === request.id}
                    >
                      {processingRequest === request.id ? (
                        <div className="notifications-page-spinner-small" />
                      ) : (
                        t("Reject")
                      )}
                    </button>
                    <button
                      onClick={() => navigate(`/social/profile/${request.id}`)}
                      className="notifications-page-follow-request-btn view"
                    >
                      {t("navbar.View_Profile")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="notifications-page-content">
          {isLoading ? (
            <div className="notifications-page-loading">
              <div className="notifications-page-spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="notifications-page-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <h3>Error loading notifications</h3>
              <p>{error}</p>
              <button
                className="notifications-page-retry-btn"
                onClick={fetchAllNotifications}
              >
                Try Again
              </button>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notifications-page-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              <h3>
                {filter === "unread"
                  ? t("notifications.empty.unreadTitle")
                  : filter === "read"
                    ? t("notifications.empty.readTitle")
                    : t("notifications.empty.defaultTitle")}
              </h3>
              <p>
                {filter === "unread"
                  ? t("notifications.empty.unreadBody")
                  : filter === "read"
                    ? t("notifications.empty.readBody")
                    : t("notifications.empty.defaultBody")}
              </p>
            </div>
          ) : (
            <div className="notifications-page-list">
              {Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
                <div key={date} className="notifications-page-date-group">
                  <div className="notifications-page-date-header">
                    <span className="notifications-page-date-label">{date}</span>
                  </div>
                  {dateNotifications.map((notification) => {
                    const isRead = notification.status === "read" || notification.is_read === true;
                    // Use parsed data from notification service
                    const parsedData = notification.parsedData || {};
                    const notificationType = notification.type;

                    // Get sender info (same logic as NotificationDropdown)
                    const getSenderInfo = () => {
                      if (notification.getSenderInfo) return notification.getSenderInfo();
                      if (parsedData.sender_name) return parsedData.sender_name;
                      if (parsedData.sender?.name) return parsedData.sender.name;
                      if (parsedData.user_name) return parsedData.user_name;
                      if (notification.sender_Data?.name) return notification.sender_Data.name;
                      return null;
                    };

                    // Get sender image (same logic as NotificationDropdown)
                    const getSenderImage = () => {
                      if (notification.getSenderImage) return notification.getSenderImage();
                      // Check notification.sender_Data first (from backend NotificationResource)
                      const senderDataImage = notification.sender_Data?.image;
                      if (senderDataImage && typeof senderDataImage === 'string' && senderDataImage.trim() !== '') {
                        return senderDataImage;
                      }
                      // Check parsedData for sender image
                      const parsedSenderImage = parsedData.sender_image;
                      if (parsedSenderImage && typeof parsedSenderImage === 'string' && parsedSenderImage.trim() !== '') {
                        return parsedSenderImage;
                      }
                      const parsedSenderObjImage = parsedData.sender?.image;
                      if (parsedSenderObjImage && typeof parsedSenderObjImage === 'string' && parsedSenderObjImage.trim() !== '') {
                        return parsedSenderObjImage;
                      }
                      // Check nested data
                      const nestedSenderImage = parsedData.data?.sender?.image;
                      if (nestedSenderImage && typeof nestedSenderImage === 'string' && nestedSenderImage.trim() !== '') {
                        return nestedSenderImage;
                      }
                      return null;
                    };

                    // Get notification main text (same logic as NotificationDropdown)
                    const getNotificationMainText = () => {
                      if (notification.processed) return notification.mainText;
                      if (notification.getMainText) return notification.getMainText();

                      const senderName = getSenderInfo() || 'Someone';
                      const reactionType = parsedData.reaction_type || 'like';
                      const reactionLabels = {
                        'like': 'liked',
                        'love': 'loved',
                        'funny': 'laughed at',
                        'haha': 'laughed at',
                        'laugh': 'laughed at',
                        'wow': 'was surprised by',
                        'sad': 'was sad about',
                        'angry': 'was angry about'
                      };

                      switch (notificationType) {
                        case "reaction":
                        case "post_reaction":
                        case "story_reaction":
                          const action = reactionLabels[reactionType] || 'reacted to';
                          const targetStr = notificationType === 'story_reaction' || parsedData.content_type?.includes('Story') ? 'story' : 'post';
                          return `${senderName} ${action} your ${targetStr}`;
                        case "comment":
                          return `${senderName} commented on your post`;
                        case "repost":
                          return `${senderName} reposted your post`;
                        case "new_follow_request":
                        case "connection_request":
                          return `${senderName} sent you a connect request`;
                        case "clinic_invitation":
                          return parsedData.clinic_name
                            ? `${senderName} invited you to join ${parsedData.clinic_name}`
                            : `${senderName} invited you to join a clinic`;
                        default:
                          return formatNotificationMessage(notification);
                      }
                    };

                    // Get notification details (same logic as NotificationDropdown)
                    const getNotificationDetails = () => {
                      if (notification.getDetails) return notification.getDetails();
                      const details = [];

                      switch (notificationType) {
                        case "appointment_created":
                        case "appointment_updated":
                        case "appointment_canceled":
                        case "appointment_restored":
                        case "appointment_status_changed":
                          if (parsedData.pet_name) details.push(`Pet: ${parsedData.pet_name}`);
                          if (parsedData.clinic_name) details.push(`Clinic: ${parsedData.clinic_name}`);
                          if (parsedData.appointment_date) {
                            try {
                              const date = new Date(parsedData.appointment_date);
                              if (!isNaN(date.getTime())) {
                                const formattedDate = date.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                });
                                const formattedTime = date.toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                });
                                details.push(`${formattedDate} at ${formattedTime}`);
                              }
                            } catch (e) { }
                          }
                          if (parsedData.status) details.push(`Status: ${parsedData.status}`);
                          break;
                        case "reaction":
                        case "post_reaction":
                        case "story_reaction":
                          const reactionType = parsedData.reaction_type || 'like';
                          // Custom emoji image paths
                          const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
                          const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
                          const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
                          const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
                          const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
                          const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";
                          const reactionEmojis = {
                            'like': HappyEmoji, 'love': LoveEmoji, 'funny': LaughEmoji, 'haha': LaughEmoji,
                            'laugh': LaughEmoji, 'wow': ShockedEmoji, 'sad': SadEmoji, 'angry': AngryEmoji
                          };
                          const emojiPath = reactionEmojis[reactionType] || HappyEmoji;
                          // Store emoji path as object so we can render it as image
                          const isStoryReact = parsedData.content_type?.includes('Story');
                          if (!isStoryReact) {
                            details.push({ type: 'reaction', emojiPath: emojiPath });
                          }
                          break;
                        case "comment":
                          if (parsedData.comment_preview || parsedData.comment_content) {
                            const preview = (parsedData.comment_preview || parsedData.comment_content || '').substring(0, 50);
                            if (preview) {
                              details.push(`"${preview}${preview.length >= 50 ? '...' : ''}"`);
                            }
                          }
                          break;
                      }
                      return details;
                    };

                    // Get content thumbnail (same logic as NotificationDropdown)
                    const getContentThumbnail = () => {
                      if (notification.getThumbnail) return notification.getThumbnail();
                      // For reactions, comments, and reposts, try to get post image
                      if (notificationType === "reaction" || notificationType === "post_reaction" || notificationType === "story_reaction" || notificationType === "comment" || notificationType === "repost") {
                        if (parsedData.post_image) return parsedData.post_image;
                        if (parsedData.image) return parsedData.image;
                        if (parsedData.media?.[0]?.url) return parsedData.media[0].url;
                        if (parsedData.data?.post_image) return parsedData.data.post_image;
                        if (parsedData.data?.image) return parsedData.data.image;
                      }
                      // For appointments and visits, try to get pet image
                      if (notificationType.includes('appointment') ||
                        notificationType.includes('outpatient') ||
                        notificationType.includes('inpatient')) {
                        if (parsedData.pet_image) return parsedData.pet_image;
                        if (parsedData.image) return parsedData.image;
                        if (parsedData.data?.pet_image) return parsedData.data.pet_image;
                      }
                      // Generic image fallback
                      if (parsedData.image && typeof parsedData.image === 'string' && parsedData.image.trim() !== '') {
                        return parsedData.image;
                      }
                      if (notification.image && typeof notification.image === 'string' && notification.image.trim() !== '') {
                        return notification.image;
                      }
                      return null;
                    };

                    const senderImage = getSenderImage();
                    const senderInfo = getSenderInfo();
                    const mainText = getNotificationMainText();
                    const details = getNotificationDetails();
                    const contentThumbnail = getContentThumbnail();

                    return (
                      <div
                        key={notification.id}
                        className={`notifications-page-item ${isRead ? "read" : "unread"}`}
                        onClick={() => handleNotificationClick(notification)}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Left blue bar for unread notifications */}
                        {!isRead && <div className="notifications-page-unread-bar" />}

                        {/* Sender profile picture */}
                        <div className="notifications-page-avatar-main">
                          {senderImage ? (
                            <img
                              src={senderImage}
                              alt={senderInfo || "Sender"}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="notifications-page-avatar-fallback"
                            style={{ display: senderImage ? 'none' : 'flex' }}
                          >
                            {senderInfo ? senderInfo.charAt(0).toUpperCase() : '?'}
                          </div>
                        </div>

                        {/* Main content area */}
                        <div className="notifications-page-item-content-main">
                          <div className="notifications-page-main-text">
                            {mainText}
                          </div>

                          {/* Additional helpful details */}
                          {details.length > 0 && (
                            <div className="notifications-page-details">
                              {details.map((detail, idx) => (
                                <span key={idx} className="notifications-page-detail-item">
                                  {typeof detail === 'object' && (detail.type === 'reaction' || detail.type === 'post_reaction' || detail.type === 'story_reaction') ? (
                                    <>
                                      <span>Reaction: </span>
                                      <img
                                        src={detail.emojiPath}
                                        alt="Reaction"
                                        style={{ width: '28px', height: '28px', verticalAlign: 'middle', marginLeft: '4px' }}
                                      />
                                    </>
                                  ) : (
                                    detail
                                  )}
                                  {idx < details.length - 1 && <span className="notifications-page-detail-separator"> • </span>}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="notifications-page-time-main">
                            {notification.time_ago || formatTime(notification.created_at || notification.createdAt)}
                          </div>

                          {/* Action buttons for clinic invitations */}
                          {notificationType === "clinic_invitation" && (
                            <div
                              className="notifications-page-clinic-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => {
                                  const invitationId =
                                    parsedData.invitation_id ||
                                    notification.target_id ||
                                    notification.id;
                                  // TODO: Implement clinic invitation handling
                                }}
                                className="notifications-page-follow-request-btn accept"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => {
                                  const invitationId =
                                    parsedData.invitation_id ||
                                    notification.target_id ||
                                    notification.id;
                                  // TODO: Implement clinic invitation handling
                                }}
                                className="notifications-page-follow-request-btn reject"
                              >
                                Reject
                              </button>
                            </div>
                          )}

                          {/* Follow Request Action Buttons */}
                          {(notification.type === "connection_request" || notification.type === "new_follow_request") && !notification.processed && (
                            <div
                              className="notifications-page-follow-request-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                className="notifications-page-follow-request-btn accept"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Get sender ID from various possible locations (same as NotificationDropdown)
                                  const senderId =
                                    notification.sender_id ||
                                    notification.parsedData?.sender_id ||
                                    notification.parsedData?.user_id ||
                                    notification.sender_Data?.id ||
                                    notification.parsedData?.content_id ||
                                    notification.target_id;



                                  if (senderId) {
                                    await handleFollowRequest(senderId, "approve", notification.id);
                                  } else {
                                    showErrorToast("Unable to process buddy request: user ID not found");
                                  }
                                }}
                                disabled={processingRequest === notification.id}
                              >
                                {processingRequest === notification.id ? (
                                  <>
                                    <div className="notifications-page-spinner-small"></div>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Accept
                                  </>
                                )}
                              </button>
                              <button
                                className="notifications-page-follow-request-btn reject"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Get sender ID from various possible locations (same as NotificationDropdown)
                                  const senderId =
                                    notification.sender_id ||
                                    notification.parsedData?.sender_id ||
                                    notification.parsedData?.user_id ||
                                    notification.sender_Data?.id ||
                                    notification.parsedData?.content_id ||
                                    notification.target_id;



                                  if (senderId) {
                                    await handleFollowRequest(senderId, "reject", notification.id);
                                  } else {
                                    showErrorToast("Unable to process buddy request: user ID not found");
                                  }
                                }}
                                disabled={processingRequest === notification.id}
                              >
                                {processingRequest === notification.id ? (
                                  <>
                                    <div className="notifications-page-spinner-small"></div>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                    Deny
                                  </>
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Get sender ID from various possible locations
                                  const senderId =
                                    notification.sender_id ||
                                    notification.parsedData?.sender_id ||
                                    notification.parsedData?.user_id ||
                                    notification.sender_Data?.id ||
                                    notification.parsedData?.content_id ||
                                    notification.target_id;

                                  if (senderId) {
                                    navigate(`/social/profile/${senderId}`);
                                  }
                                }}
                                className="notifications-page-follow-request-btn view"
                              >
                                View Profile
                              </button>
                            </div>
                          )}

                          {/* Guardianship Invitation Action Buttons */}
                          {notificationType === "guardianship_invitation" && !notification.processed && (
                            <div
                              className="notifications-page-follow-request-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                className="notifications-page-follow-request-btn accept"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const requestId = parsedData.request_id || notification.target_id;
                                  if (requestId) {
                                    await handleGuardianshipResponse(requestId, "approve", notification.id);
                                  } else {
                                    showErrorToast("Unable to process invitation: Request ID not found");
                                  }
                                }}
                                disabled={processingRequest === notification.id}
                              >
                                {processingRequest === notification.id ? (
                                  <>
                                    <div className="notifications-page-spinner-small"></div>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Accept
                                  </>
                                )}
                              </button>
                              <button
                                className="notifications-page-follow-request-btn reject"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const requestId = parsedData.request_id || notification.target_id;
                                  if (requestId) {
                                    await handleGuardianshipResponse(requestId, "reject", notification.id);
                                  } else {
                                    showErrorToast("Unable to process invitation: Request ID not found");
                                  }
                                }}
                                disabled={processingRequest === notification.id}
                              >
                                {processingRequest === notification.id ? (
                                  <>
                                    <div className="notifications-page-spinner-small"></div>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                    Deny
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Content thumbnail on the right */}
                        {contentThumbnail && (
                          <div className="notifications-page-thumbnail">
                            <img
                              src={contentThumbnail}
                              alt="Content preview"
                              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !isLoading && !error && filteredNotifications.length > 0 && (
            <div className="notifications-page-load-more">
              <button
                onClick={() => fetchAllNotifications(true)}
                disabled={isLoadMore}
                className="notifications-load-more-btn"
                style={{
                  display: 'block',
                  margin: '20px auto',
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: colors.primary,
                  border: `1px solid ${colors.primary}`,
                  borderRadius: '20px',
                  cursor: isLoadMore ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '14px',
                  opacity: isLoadMore ? 0.7 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                {isLoadMore ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="notifications-page-spinner-small" style={{ borderTopColor: colors.primary }}></div>
                    Loading...
                  </span>
                ) : (
                  t("Load More") || "Load More"
                )}
              </button>
            </div>
          )}

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NotificationsPage;

